import { useEffect, useState } from "react";
import {
  loadPendingMatch,
  reconnectSecondsLeft,
  setReconnectIntent,
  clearPendingMatch,
  graceSecForDrop,
  type PendingPongMatch,
} from "../lib/pongReconnect";
import GameIcon, { gameMeta } from "./GameIcon";

interface PongReconnectCardProps {
  /** The currently logged-in wallet — used to scope the card to the
   *  account that owns the saved match. Hides the card otherwise. */
  walletAddress: string | null;
  /** Asks the parent to navigate to the /pong route. The page itself
   *  will read the reconnect intent and call `client.reconnect()`. */
  onReconnect: () => void;
}

const GAME: "pong" = "pong";

/** Deterministic gradient + initials for an opponent avatar disc.
 *  Mirrors the in-game `walletColors()` so the same opponent looks
 *  identical between the live HUD and the reconnect card. */
function walletColors(addr: string | null | undefined) {
  if (!addr) return { from: "#444", to: "#222", initials: "?" };
  const h1 = parseInt(addr.slice(2, 8), 16) % 360;
  const h2 = (h1 + 120 + (parseInt(addr.slice(8, 12), 16) % 60)) % 360;
  return {
    from: `hsl(${h1} 70% 55%)`,
    to: `hsl(${h2} 65% 35%)`,
    initials: addr.slice(2, 4).toUpperCase(),
  };
}

function abbrev(addr: string | null | undefined): string {
  if (!addr) return "Anon";
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

/**
 * Floating bottom-right card surfaced whenever there is a pending
 * Pong match in localStorage owned by the current wallet. Shows the
 * game icon, pot value, opponent avatar, and a countdown ring around
 * a Reconnect button. Polls every 250 ms so the second-by-second
 * countdown looks alive (1 s tick reads like a digital clock; 4×/s
 * gives a smoother urgent feel as the ring drains).
 */
export default function PongReconnectCard({
  walletAddress,
  onReconnect,
}: PongReconnectCardProps) {
  const [pending, setPending] = useState<PendingPongMatch | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    const refresh = () => {
      const m = loadPendingMatch();
      if (!m) {
        setPending(null);
        setSecondsLeft(0);
        return;
      }
      const left = reconnectSecondsLeft(m);
      if (left <= 0) {
        clearPendingMatch();
        setPending(null);
        setSecondsLeft(0);
        return;
      }
      setPending(m);
      setSecondsLeft(left);
    };
    refresh();
    const id = setInterval(refresh, 250);
    return () => clearInterval(id);
  }, []);

  if (!pending) return null;
  if (!walletAddress) return null;
  if (pending.walletAddress.toLowerCase() !== walletAddress.toLowerCase()) {
    return null;
  }

  const meta = gameMeta(GAME);
  const totalPot = pending.stake * 2;
  const oppCol = walletColors(pending.opponentWallet);
  const totalGrace = graceSecForDrop(pending.dropCount);
  const fraction = totalGrace > 0 ? secondsLeft / totalGrace : 0;
  const urgent = secondsLeft <= 15;
  const veryUrgent = secondsLeft <= 5;

  // Stroke-dash for the circular countdown ring (r=22, circumference 138).
  const RADIUS = 22;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE * (1 - fraction);

  const handleClick = () => {
    setReconnectIntent();
    onReconnect();
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearPendingMatch();
    setPending(null);
  };

  return (
    <div
      className="fixed bottom-5 right-5 z-[90] w-[336px] rounded-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
      style={{
        background: "linear-gradient(160deg, rgba(26,26,26,0.97) 0%, rgba(15,15,15,0.97) 100%)",
        border: `1px solid ${urgent ? "#ef4444" : meta.accent}55`,
        boxShadow: urgent
          ? "0 12px 40px rgba(239,68,68,0.30), 0 0 0 1px rgba(239,68,68,0.15)"
          : `0 12px 40px ${meta.accent}38, 0 0 0 1px ${meta.accent}15`,
        backdropFilter: "blur(14px)",
      }}
    >
      {/* Header strip — game name, live indicator, dismiss */}
      <div
        className="flex items-center justify-between px-4 py-2.5 border-b border-white/5"
        style={{
          background: `linear-gradient(90deg, ${meta.accent}22 0%, transparent 70%)`,
        }}
      >
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-7 h-7 rounded-lg" style={{ background: `${meta.accent}25`, color: meta.accent }}>
            <GameIcon game={GAME} className="w-4 h-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold tracking-wider text-white/95 uppercase">{meta.label}</span>
            <span className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/40">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live match
            </span>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/30 hover:text-white/70 text-[11px] uppercase tracking-wider px-1 transition"
          title="Forfeit and dismiss"
        >
          ✕
        </button>
      </div>

      {/* Body — pot, opponent, ring + reconnect */}
      <div className="px-4 py-4 flex items-center gap-4">
        {/* Countdown ring around the reconnect button */}
        <button
          onClick={handleClick}
          className="relative w-[58px] h-[58px] flex-shrink-0 group focus:outline-none"
          aria-label="Reconnect"
        >
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 58 58">
            <circle cx="29" cy="29" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
            <circle
              cx="29"
              cy="29"
              r={RADIUS}
              fill="none"
              stroke={veryUrgent ? "#ef4444" : urgent ? "#fbbf24" : meta.accent}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              className="transition-[stroke-dashoffset] duration-200 ease-linear"
              style={veryUrgent ? { filter: "drop-shadow(0 0 6px rgba(239,68,68,0.6))" } : undefined}
            />
          </svg>
          <span
            className="absolute inset-1.5 rounded-full flex items-center justify-center text-white font-bold text-[13px] tracking-wider transition-transform group-hover:scale-105 group-active:scale-95"
            style={{
              background: `linear-gradient(140deg, ${meta.accent} 0%, ${meta.accent2} 100%)`,
              boxShadow: `0 4px 14px ${meta.accent}66`,
            }}
          >
            {secondsLeft <= 99 ? secondsLeft : "99+"}
          </span>
        </button>

        {/* Right column — pot + opponent */}
        <div className="flex-1 min-w-0">
          {/* Pot */}
          <div className="flex items-baseline gap-1.5 mb-1">
            <span className="text-2xl font-extrabold text-white tracking-tight">
              ${totalPot.toFixed(2)}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">pot</span>
          </div>
          {/* Opponent row */}
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-bold text-white/95"
              style={{ background: `linear-gradient(140deg, ${oppCol.from}, ${oppCol.to})` }}
            >
              {oppCol.initials}
            </div>
            <span className="font-mono text-[11px] text-white/60 truncate">vs {abbrev(pending.opponentWallet)}</span>
          </div>
          {/* Reconnect CTA — text version of the same action as the ring */}
          <button
            onClick={handleClick}
            className="w-full py-2 rounded-lg text-white text-xs font-bold uppercase tracking-wider transition-all hover:shadow-lg active:scale-[0.98]"
            style={{
              background: `linear-gradient(90deg, ${meta.accent} 0%, ${meta.accent2} 100%)`,
              boxShadow: `0 4px 14px ${meta.accent}40`,
            }}
          >
            Reconnect
          </button>
        </div>
      </div>
    </div>
  );
}
